import React, { useState, useEffect, useCallback } from 'react';
import {
    Landmark, Plus, RefreshCw, Trash2, Loader2, TrendingUp, TrendingDown,
    ArrowUpRight, ArrowDownLeft, Filter, Search, Tag, Clock, AlertTriangle,
    Wallet, PieChart, BarChart3, ChevronDown, X, Edit2, Check, Bell, Zap, Settings,
    Download, FileText, FileSpreadsheet, Upload, Shield, Wifi, WifiOff, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell, PieChart as RPieChart, Pie } from 'recharts';
import { ContextualTour } from '../../components/ContextualTour';
import { API } from '../../config/apiBase';

const PERIODS = [
    { key: 'today', label: 'Hoje' },
    { key: 'week', label: 'Semana' },
    { key: 'month', label: 'Mes' },
    { key: 'quarter', label: 'Trimestre' },
];
const PIE_COLORS = ['#7DB3FF', '#EF4444', '#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899', '#06B6D4', '#F97316', '#84CC16'];

const TOUR_STEPS = [
    { target: '[data-testid="of-kpis"]', title: 'Visao Geral', content: 'Acompanhe entradas, saidas e saldo liquido do periodo selecionado.' },
    { target: '[data-testid="of-connections"]', title: 'Contas Conectadas', content: 'Conecte suas contas bancarias via Open Finance para importar extratos automaticamente.' },
    { target: '[data-testid="of-evolution-chart"]', title: 'Evolucao Financeira', content: 'Acompanhe a evolucao de entradas e saidas ao longo do periodo.' },
];

function CurrencyDisplay({ value, className = '' }) {
    const formatted = Math.abs(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    return <span className={className}>{value < 0 ? `-${formatted}` : formatted}</span>;
}

function CustomTooltip({ active, payload, label }) {
    if (!active || !payload?.length) return null;
    return (
        <div style={{ background: '#0D2C63', border: '1px solid rgba(140,180,255,0.30)', borderRadius: 8, padding: '8px 12px', fontSize: 11 }}>
            <p style={{ color: '#999', marginBottom: 4 }}>{label}</p>
            {payload.map((p, i) => (
                <p key={i} style={{ color: p.color }}>{p.name}: <strong>R$ {Math.abs(p.value).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</strong></p>
            ))}
        </div>
    );
}

export default function OpenFinancePage() {
    const [period, setPeriod] = useState('month');
    const [dashboard, setDashboard] = useState(null);
    const [connections, setConnections] = useState([]);
    const [transactions, setTransactions] = useState([]);
    const [txnTotal, setTxnTotal] = useState(0);
    const [txnPage, setTxnPage] = useState(1);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [syncing, setSyncing] = useState(null);
    const [filterType, setFilterType] = useState('');
    const [filterCategory, setFilterCategory] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [editingTxn, setEditingTxn] = useState(null);
    const [editCat, setEditCat] = useState('');
    const [activeTab, setActiveTab] = useState('dashboard');
    const [rules, setRules] = useState([]);
    const [alerts, setAlerts] = useState([]);
    const [triggeredAlerts, setTriggeredAlerts] = useState([]);
    const [newRule, setNewRule] = useState({ pattern: '', category: '', match_type: 'contains' });
    const [newAlert, setNewAlert] = useState({ name: '', category: '', threshold: '', period: 'month' });
    const [showRuleForm, setShowRuleForm] = useState(false);
    const [showAlertForm, setShowAlertForm] = useState(false);
    const [execReport, setExecReport] = useState(null);
    const [exporting, setExporting] = useState(null);
    const [importFile, setImportFile] = useState(null);
    const [importing, setImporting] = useState(false);
    const [importResult, setImportResult] = useState(null);
    const [belvoStatus, setBelvoStatus] = useState(null);
    const [belvoLoading, setBelvoLoading] = useState(false);
    const [belvoExpanded, setBelvoExpanded] = useState(false);

    const token = localStorage.getItem('token') || sessionStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}` };

    const fetchBelvoStatus = useCallback(async () => {
        setBelvoLoading(true);
        try {
            const r = await fetch(`${API}/openfinance/belvo-status`, { headers });
            if (r.ok) setBelvoStatus(await r.json());
            else setBelvoStatus({ _error: true });
        } catch { setBelvoStatus({ _error: true }); }
        setBelvoLoading(false);
    }, []);

    useEffect(() => { fetchBelvoStatus(); }, [fetchBelvoStatus]);

    const fetchAll = useCallback(async () => {
        try {
            const [dashRes, connRes, txnRes, catRes, rulesRes, alertsRes, trigRes, execRes] = await Promise.all([
                fetch(`${API}/openfinance/dashboard?period=${period}`, { headers }),
                fetch(`${API}/openfinance/connections`, { headers }),
                fetch(`${API}/openfinance/transactions?period=${period}&type=${filterType}&category=${filterCategory}&page=${txnPage}`, { headers }),
                fetch(`${API}/openfinance/categories`, { headers }),
                fetch(`${API}/openfinance/rules`, { headers }),
                fetch(`${API}/openfinance/alerts`, { headers }),
                fetch(`${API}/openfinance/alerts/check`, { headers }),
                fetch(`${API}/openfinance/report/executive?period=${period}`, { headers }),
            ]);
            const [dashData, connData, txnData, catData, rulesData, alertsData, trigData, execData] = await Promise.all([
                dashRes.ok ? dashRes.json() : null,
                connRes.ok ? connRes.json() : { connections: [] },
                txnRes.ok ? txnRes.json() : { transactions: [], total: 0 },
                catRes.ok ? catRes.json() : { categories: [] },
                rulesRes.ok ? rulesRes.json() : { rules: [] },
                alertsRes.ok ? alertsRes.json() : { alerts: [] },
                trigRes.ok ? trigRes.json() : { triggered: [] },
                execRes.ok ? execRes.json() : null,
            ]);
            setDashboard(dashData);
            setConnections(connData.connections || []);
            setTransactions(txnData.transactions || []);
            setTxnTotal(txnData.total || 0);
            setCategories(catData.categories || []);
            setRules(rulesData.rules || []);
            setAlerts(alertsData.alerts || []);
            setTriggeredAlerts(trigData.triggered || []);
            setExecReport(execData);
        } catch { toast.error('Erro ao carregar dados'); }
        setLoading(false);
    }, [period, filterType, filterCategory, txnPage]);

    useEffect(() => { fetchAll(); }, [fetchAll]);

    const handleConnect = async () => {
        try {
            const r = await fetch(`${API}/openfinance/connect-token`, { method: 'POST', headers });
            const data = await r.json();
            if (data.demo) {
                // Demo mode: simulate connection
                const connR = await fetch(`${API}/openfinance/connections`, {
                    method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
                    body: JSON.stringify({ itemId: `demo_${Date.now()}`, connectorName: 'Banco Demo', connectorLogo: '' })
                });
                if (connR.ok) {
                    toast.success('Conta demo conectada! Transacoes de exemplo importadas.');
                    setLoading(true);
                    fetchAll();
                }
            } else {
                toast.info('Widget Pluggy seria aberto aqui com credenciais reais.');
            }
        } catch { toast.error('Erro ao conectar'); }
    };

    const handleSync = async (connId) => {
        setSyncing(connId);
        try {
            const r = await fetch(`${API}/openfinance/connections/${connId}/sync`, { method: 'POST', headers });
            const data = await r.json();
            if (r.ok) {
                toast.success(`${data.synced} transacoes sincronizadas!`);
                fetchAll();
            } else toast.error(data.detail || 'Erro ao sincronizar');
        } catch { toast.error('Erro de conexao'); }
        setSyncing(null);
    };

    const handleDisconnect = async (connId) => {
        if (!window.confirm('Tem certeza? Isso remove a conexao e todas as transacoes importadas.')) return;
        try {
            const r = await fetch(`${API}/openfinance/connections/${connId}`, { method: 'DELETE', headers });
            if (r.ok) {
                toast.success('Conexao removida');
                fetchAll();
            }
        } catch { toast.error('Erro'); }
    };

    const handleUpdateCategory = async (externalId) => {
        try {
            await fetch(`${API}/openfinance/transactions/${externalId}`, {
                method: 'PATCH', headers: { ...headers, 'Content-Type': 'application/json' },
                body: JSON.stringify({ category_manual: editCat })
            });
            toast.success('Categoria atualizada');
            setEditingTxn(null);
            fetchAll();
        } catch { toast.error('Erro'); }
    };

    const handleCreateRule = async () => {
        if (!newRule.pattern || !newRule.category) return toast.error('Preencha padrao e categoria');
        try {
            const r = await fetch(`${API}/openfinance/rules`, {
                method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
                body: JSON.stringify(newRule)
            });
            if (r.ok) { toast.success('Regra criada'); setNewRule({ pattern: '', category: '', match_type: 'contains' }); setShowRuleForm(false); fetchAll(); }
        } catch { toast.error('Erro'); }
    };

    const handleDeleteRule = async (ruleId) => {
        try {
            await fetch(`${API}/openfinance/rules/${ruleId}`, { method: 'DELETE', headers });
            toast.success('Regra removida'); fetchAll();
        } catch { toast.error('Erro'); }
    };

    const handleApplyRules = async () => {
        try {
            const r = await fetch(`${API}/openfinance/rules/apply`, { method: 'POST', headers });
            const data = await r.json();
            toast.success(`${data.applied} transacoes categorizadas`); fetchAll();
        } catch { toast.error('Erro'); }
    };

    const handleCreateAlert = async () => {
        if (!newAlert.name || !newAlert.threshold) return toast.error('Preencha nome e limite');
        try {
            const r = await fetch(`${API}/openfinance/alerts`, {
                method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...newAlert, threshold: parseFloat(newAlert.threshold) })
            });
            if (r.ok) { toast.success('Alerta criado'); setNewAlert({ name: '', category: '', threshold: '', period: 'month' }); setShowAlertForm(false); fetchAll(); }
        } catch { toast.error('Erro'); }
    };

    const handleDeleteAlert = async (alertId) => {
        try {
            await fetch(`${API}/openfinance/alerts/${alertId}`, { method: 'DELETE', headers });
            toast.success('Alerta removido'); fetchAll();
        } catch { toast.error('Erro'); }
    };

    const handleExport = async (format) => {
        setExporting(format);
        try {
            const r = await fetch(`${API}/openfinance/export/${format}?period=${period}`, { headers });
            if (!r.ok) throw new Error();
            const blob = await r.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `relatorio_financeiro_${period}.${format}`;
            a.click();
            URL.revokeObjectURL(url);
            toast.success(`${format.toUpperCase()} exportado`);
        } catch { toast.error('Erro na exportacao'); }
        setExporting(null);
    };

    const handleImportFile = async () => {
        if (!importFile) return;
        setImporting(true);
        setImportResult(null);
        try {
            const formData = new FormData();
            formData.append('file', importFile);
            const r = await fetch(`${API}/openfinance/import`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: formData });
            const d = await r.json();
            if (r.ok) {
                setImportResult(d);
                toast.success(d.message);
                setImportFile(null);
                fetchAll();
            } else {
                toast.error(d.detail || 'Erro na importacao');
                setImportResult({ ok: false, message: d.detail });
            }
        } catch { toast.error('Erro de conexao'); }
        setImporting(false);
    };

    const filteredTxns = transactions.filter(t =>
        !searchTerm || t.description?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (loading) return <div className="flex justify-center py-12"><Loader2 className="animate-spin" size={24} style={{ color: 'var(--sv-accent)' }} /></div>;

    const d = dashboard || {};

    return (
        <div className="space-y-5" data-testid="open-finance-page">
            <ContextualTour pageKey="open-finance" steps={TOUR_STEPS} />

            {/* Header */}
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                    <h1 className="text-lg font-bold" style={{ color: 'var(--text)', fontFamily: "var(--font-display)" }}>Open Finance</h1>
                    <p className="text-xs mt-0.5" style={{ color: 'var(--muted)' }}>Gestao financeira integrada com contas bancarias</p>
                </div>
                {triggeredAlerts.length > 0 && (
                    <button onClick={() => setActiveTab('alertas')} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium animate-pulse" style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', color: '#EF4444' }} data-testid="of-alert-badge">
                        <Bell size={12} /> {triggeredAlerts.length} alerta(s)
                    </button>
                )}
            </div>

            {/* Belvo Integration Status Card */}
            {belvoStatus && (
                <div data-testid="belvo-status-card" className="rounded-xl overflow-hidden transition-all" style={{
                    background: 'var(--surface)',
                    border: `1px solid ${belvoStatus._error ? 'rgba(239,68,68,0.4)' : belvoStatus.credentials_valid && !belvoStatus.demo_mode ? 'rgba(16,185,129,0.4)' : belvoStatus.configured ? 'rgba(245,158,11,0.4)' : 'rgba(239,68,68,0.4)'}`,
                }}>
                    <button onClick={() => setBelvoExpanded(!belvoExpanded)} className="w-full flex items-center justify-between px-4 py-2.5 cursor-pointer" data-testid="belvo-status-toggle">
                        <div className="flex items-center gap-3">
                            <div className="relative">
                                <Shield size={16} style={{ color: belvoStatus._error ? '#EF4444' : belvoStatus.credentials_valid && !belvoStatus.demo_mode ? '#10B981' : belvoStatus.configured ? '#F59E0B' : '#EF4444' }} />
                                <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2" style={{
                                    borderColor: 'var(--surface)',
                                    background: belvoStatus._error ? '#EF4444' : belvoStatus.credentials_valid && !belvoStatus.demo_mode ? '#10B981' : belvoStatus.configured ? '#F59E0B' : '#EF4444',
                                }} />
                            </div>
                            <div className="text-left">
                                <span className="text-xs font-semibold" style={{ color: 'var(--text)' }}>Status Open Finance</span>
                                <span className="ml-2 text-[10px] px-2 py-0.5 rounded-full font-medium" style={{
                                    background: belvoStatus._error ? 'rgba(239,68,68,0.15)' : belvoStatus.credentials_valid && !belvoStatus.demo_mode ? 'rgba(16,185,129,0.15)' : belvoStatus.configured ? 'rgba(245,158,11,0.15)' : 'rgba(239,68,68,0.15)',
                                    color: belvoStatus._error ? '#EF4444' : belvoStatus.credentials_valid && !belvoStatus.demo_mode ? '#10B981' : belvoStatus.configured ? '#F59E0B' : '#EF4444',
                                }}>
                                    {belvoStatus._error ? 'Indisponivel' : belvoStatus.credentials_valid && !belvoStatus.demo_mode ? (belvoStatus.env === 'production' ? 'Ativo Producao' : 'Ativo Sandbox') : belvoStatus.demo_mode ? 'Modo Demo' : 'Nao configurado'}
                                </span>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <button onClick={(e) => { e.stopPropagation(); fetchBelvoStatus(); }} disabled={belvoLoading} className="p-1.5 rounded-lg transition-all hover:opacity-80" style={{ background: 'var(--bg)' }} data-testid="belvo-status-refresh">
                                <RefreshCw size={12} className={belvoLoading ? 'animate-spin' : ''} style={{ color: 'var(--muted)' }} />
                            </button>
                            <ChevronRight size={14} className="transition-transform" style={{ color: 'var(--muted)', transform: belvoExpanded ? 'rotate(90deg)' : 'none' }} />
                        </div>
                    </button>

                    {belvoExpanded && !belvoStatus._error && (
                        <div className="px-4 pb-3 pt-0">
                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2" data-testid="belvo-status-details">
                                {[
                                    { label: 'Provider', value: (belvoStatus.provider || '-').toUpperCase(), icon: Landmark },
                                    { label: 'Ambiente', value: belvoStatus.env === 'production' ? 'Producao' : 'Sandbox', icon: belvoStatus.env === 'production' ? Wifi : Settings },
                                    { label: 'Configurado', value: belvoStatus.configured ? 'Sim' : 'Nao', icon: Check, ok: belvoStatus.configured },
                                    { label: 'Credenciais', value: belvoStatus.credentials_valid ? 'Validas' : 'Invalidas', icon: Shield, ok: belvoStatus.credentials_valid },
                                    { label: 'Demo Mode', value: belvoStatus.demo_mode ? 'Ativo' : 'Inativo', icon: AlertTriangle, ok: !belvoStatus.demo_mode },
                                    { label: 'Webhook', value: belvoStatus.webhook_auth_configured ? 'Protegido' : 'Aberto', icon: belvoStatus.webhook_auth_configured ? Shield : WifiOff },
                                ].map((item, i) => (
                                    <div key={i} className="flex items-center gap-2 px-3 py-2 rounded-lg" style={{ background: 'var(--bg)' }}>
                                        <item.icon size={12} style={{ color: item.ok === true ? '#10B981' : item.ok === false ? '#F59E0B' : 'var(--muted)', flexShrink: 0 }} />
                                        <div className="min-w-0">
                                            <div className="text-[9px] uppercase tracking-wider" style={{ color: 'var(--muted)' }}>{item.label}</div>
                                            <div className="text-[11px] font-semibold truncate" style={{ color: item.ok === true ? '#10B981' : item.ok === false ? '#F59E0B' : 'var(--text)' }}>{item.value}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            {belvoStatus.demo_mode && (
                                <div className="mt-2 px-3 py-2 rounded-lg text-[10px]" style={{ background: 'rgba(245,158,11,0.08)', color: '#F59E0B' }} data-testid="belvo-status-hint">
                                    Insira credenciais Belvo reais no .env (BELVO_SECRET_ID e BELVO_SECRET_PASSWORD) para ativar a integracao bancaria real.
                                </div>
                            )}
                            {belvoStatus.webhook_routes && (
                                <div className="mt-2 text-[10px]" style={{ color: 'var(--muted)' }}>
                                    Webhooks: {belvoStatus.webhook_routes.join(' | ')}
                                </div>
                            )}
                        </div>
                    )}

                    {belvoExpanded && belvoStatus._error && (
                        <div className="px-4 pb-3 pt-0">
                            <div className="px-3 py-2 rounded-lg text-[10px]" style={{ background: 'rgba(239,68,68,0.08)', color: '#EF4444' }} data-testid="belvo-status-error">
                                Erro ao consultar status da integracao Belvo. Verifique se o backend esta online.
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Tabs */}
            <div className="flex gap-1 p-1 rounded-xl" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }} data-testid="of-tabs">
                {[
                    { key: 'dashboard', label: 'Dashboard', icon: BarChart3 },
                    { key: 'relatorios', label: 'Relatorios', icon: FileText },
                    { key: 'importar', label: 'Importar', icon: Upload },
                    { key: 'regras', label: 'Regras', icon: Zap },
                    { key: 'alertas', label: 'Alertas', icon: Bell },
                ].map(tab => (
                    <button key={tab.key} onClick={() => setActiveTab(tab.key)} className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium flex-1 justify-center transition-all" style={{
                        background: activeTab === tab.key ? 'var(--sv-secondary)' : 'transparent',
                        color: activeTab === tab.key ? 'var(--sv-on-secondary)' : 'var(--muted)',
                    }} data-testid={`of-tab-${tab.key}`}>
                        <tab.icon size={13} /> {tab.label}
                        {tab.key === 'alertas' && triggeredAlerts.length > 0 && (
                            <span className="ml-1 w-4 h-4 rounded-full text-[9px] flex items-center justify-center font-bold" style={{ background: '#EF4444', color: '#fff' }}>{triggeredAlerts.length}</span>
                        )}
                    </button>
                ))}
            </div>

            {activeTab === 'dashboard' && (<>
            {/* Period filters + Export buttons */}
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex gap-2 flex-wrap">
                    {PERIODS.map(p => (
                        <button key={p.key} onClick={() => { setPeriod(p.key); setTxnPage(1); }} className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all" style={{
                            background: period === p.key ? 'var(--sv-secondary)' : 'var(--surface)',
                            color: period === p.key ? 'var(--sv-on-secondary)' : 'var(--muted)',
                            border: `1px solid ${period === p.key ? 'var(--sv-secondary)' : 'var(--border)'}`,
                        }} data-testid={`of-period-${p.key}`}>{p.label}</button>
                    ))}
                </div>
                <div className="flex gap-1.5">
                    <button onClick={() => handleExport('csv')} disabled={!!exporting} className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs" style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text)' }} data-testid="of-export-csv">
                        {exporting === 'csv' ? <Loader2 size={11} className="animate-spin" /> : <FileSpreadsheet size={11} />} CSV
                    </button>
                    <button onClick={() => handleExport('pdf')} disabled={!!exporting} className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs" style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text)' }} data-testid="of-export-pdf">
                        {exporting === 'pdf' ? <Loader2 size={11} className="animate-spin" /> : <FileText size={11} />} PDF
                    </button>
                </div>
            </div>

            {/* KPI Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="of-kpis">
                <div className="rounded-xl p-4" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                    <div className="flex items-center gap-2 mb-2">
                        <ArrowUpRight size={14} style={{ color: '#10B981' }} />
                        <span className="text-[10px] font-medium" style={{ color: 'var(--muted)' }}>Entradas</span>
                    </div>
                    <CurrencyDisplay value={d.entradas || 0} className="text-sm font-bold" style={{ color: '#10B981' }} />
                </div>
                <div className="rounded-xl p-4" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                    <div className="flex items-center gap-2 mb-2">
                        <ArrowDownLeft size={14} style={{ color: '#EF4444' }} />
                        <span className="text-[10px] font-medium" style={{ color: 'var(--muted)' }}>Saidas</span>
                    </div>
                    <CurrencyDisplay value={d.saidas || 0} className="text-sm font-bold" style={{ color: '#EF4444' }} />
                </div>
                <div className="rounded-xl p-4" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                    <div className="flex items-center gap-2 mb-2">
                        <Wallet size={14} style={{ color: 'var(--sv-accent)' }} />
                        <span className="text-[10px] font-medium" style={{ color: 'var(--muted)' }}>Saldo Liquido</span>
                    </div>
                    <CurrencyDisplay value={d.saldo_liquido || 0} className="text-sm font-bold" style={{ color: (d.saldo_liquido || 0) >= 0 ? '#10B981' : '#EF4444' }} />
                </div>
                <div className="rounded-xl p-4" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                    <div className="flex items-center gap-2 mb-2">
                        <Landmark size={14} style={{ color: '#3B82F6' }} />
                        <span className="text-[10px] font-medium" style={{ color: 'var(--muted)' }}>Saldo Bancario</span>
                    </div>
                    <CurrencyDisplay value={d.saldo_consolidado || 0} className="text-sm font-bold" style={{ color: 'var(--text)' }} />
                </div>
            </div>

            {/* Bank Connections */}
            <div className="rounded-xl p-4" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }} data-testid="of-connections">
                <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                        <Landmark size={14} style={{ color: 'var(--sv-accent)' }} />
                        <h3 className="text-xs font-semibold" style={{ color: 'var(--text)' }}>Contas Conectadas</h3>
                    </div>
                    <button onClick={handleConnect} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium" style={{ background: 'var(--sv-gold-btn)', color: 'var(--sv-on-action)' }} data-testid="of-connect-btn">
                        <Plus size={12} /> Conectar Conta
                    </button>
                </div>
                {connections.length === 0 ? (
                    <p className="text-xs py-4 text-center" style={{ color: 'var(--muted)' }}>Nenhuma conta conectada. Clique em "Conectar Conta" para comecar.</p>
                ) : (
                    <div className="space-y-2">
                        {connections.map(c => (
                            <div key={c.connection_id} className="flex items-center gap-3 p-3 rounded-lg" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)' }}>
                                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(59,130,246,0.1)' }}>
                                    <Landmark size={14} style={{ color: '#3B82F6' }} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-xs font-semibold truncate" style={{ color: 'var(--text)' }}>{c.connector_name || 'Banco'}</p>
                                    <p className="text-[10px]" style={{ color: 'var(--muted)' }}>
                                        {c.accounts?.length || 0} conta(s) | Sync: {c.last_sync ? new Date(c.last_sync).toLocaleString('pt-BR') : 'Nunca'}
                                    </p>
                                </div>
                                <button onClick={() => handleSync(c.connection_id)} disabled={syncing === c.connection_id} className="p-2 rounded-lg" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }} data-testid={`of-sync-${c.connection_id}`}>
                                    <RefreshCw size={12} className={syncing === c.connection_id ? 'animate-spin' : ''} style={{ color: 'var(--muted)' }} />
                                </button>
                                <button onClick={() => handleDisconnect(c.connection_id)} className="p-2 rounded-lg" style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }} data-testid={`of-disconnect-${c.connection_id}`}>
                                    <Trash2 size={12} style={{ color: '#EF4444' }} />
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Charts */}
            {dashboard && (d.evolution?.length > 0 || d.categories?.length > 0) && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {/* Evolution Chart */}
                    <div className="rounded-xl p-4" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }} data-testid="of-evolution-chart">
                        <div className="flex items-center gap-2 mb-3">
                            <BarChart3 size={14} style={{ color: 'var(--sv-accent)' }} />
                            <h3 className="text-xs font-semibold" style={{ color: 'var(--text)' }}>Evolucao Financeira</h3>
                        </div>
                        <ResponsiveContainer width="100%" height={200}>
                            <BarChart data={d.evolution?.map(e => ({
                                date: e.date?.substring(5) || '',
                                entradas: Math.round(e.entradas),
                                saidas: Math.round(e.saidas),
                            })).slice(-15)} margin={{ top: 5, right: 5, bottom: 5, left: -10 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                                <XAxis dataKey="date" tick={{ fontSize: 9, fill: '#888' }} />
                                <YAxis tick={{ fontSize: 9, fill: '#888' }} />
                                <Tooltip content={<CustomTooltip />} />
                                <Bar dataKey="entradas" fill="#10B981" radius={[3, 3, 0, 0]} name="Entradas" />
                                <Bar dataKey="saidas" fill="#EF4444" radius={[3, 3, 0, 0]} name="Saidas" />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>

                    {/* Category Pie Chart */}
                    <div className="rounded-xl p-4" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }} data-testid="of-category-chart">
                        <div className="flex items-center gap-2 mb-3">
                            <PieChart size={14} style={{ color: 'var(--sv-accent)' }} />
                            <h3 className="text-xs font-semibold" style={{ color: 'var(--text)' }}>Gastos por Categoria</h3>
                        </div>
                        {d.categories?.filter(c => c.saidas > 0).length > 0 ? (
                            <ResponsiveContainer width="100%" height={200}>
                                <RPieChart>
                                    <Pie
                                        data={d.categories.filter(c => c.saidas > 0).slice(0, 8).map(c => ({ name: c.name, value: Math.round(c.saidas) }))}
                                        cx="50%" cy="50%" innerRadius={40} outerRadius={75}
                                        dataKey="value" paddingAngle={2} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                                        labelLine={{ stroke: '#555' }}
                                    >
                                        {d.categories.filter(c => c.saidas > 0).slice(0, 8).map((_, i) => (
                                            <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip formatter={(v) => `R$ ${v.toLocaleString('pt-BR')}`} contentStyle={{ background: '#0D2C63', border: '1px solid rgba(140,180,255,0.30)', borderRadius: 8, fontSize: 11 }} />
                                </RPieChart>
                            </ResponsiveContainer>
                        ) : (
                            <div className="flex items-center justify-center h-[200px]"><p className="text-xs" style={{ color: 'var(--muted)' }}>Sem dados de gastos</p></div>
                        )}
                    </div>
                </div>
            )}

            {/* Transactions */}
            <div className="rounded-xl p-4" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }} data-testid="of-transactions">
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                    <div className="flex items-center gap-2">
                        <Clock size={14} style={{ color: 'var(--sv-accent)' }} />
                        <h3 className="text-xs font-semibold" style={{ color: 'var(--text)' }}>Transacoes ({txnTotal})</h3>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                        <div className="relative">
                            <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2" style={{ color: 'var(--muted)' }} />
                            <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Buscar..."
                                className="pl-7 pr-2 py-1.5 rounded-lg text-xs" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)', width: 140 }} />
                        </div>
                        <select value={filterType} onChange={e => { setFilterType(e.target.value); setTxnPage(1); }}
                            className="px-2 py-1.5 rounded-lg text-xs" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }}>
                            <option value="">Todos</option>
                            <option value="entrada">Entradas</option>
                            <option value="saida">Saidas</option>
                        </select>
                        <select value={filterCategory} onChange={e => { setFilterCategory(e.target.value); setTxnPage(1); }}
                            className="px-2 py-1.5 rounded-lg text-xs" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }}>
                            <option value="">Todas categorias</option>
                            {categories.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                    </div>
                </div>

                {filteredTxns.length === 0 ? (
                    <p className="text-xs py-6 text-center" style={{ color: 'var(--muted)' }}>
                        {connections.length === 0 ? 'Conecte uma conta bancaria para ver transacoes.' : 'Nenhuma transacao encontrada para os filtros selecionados.'}
                    </p>
                ) : (
                    <div className="space-y-1">
                        {filteredTxns.map(t => (
                            <div key={t.external_id} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-white/[0.02] transition-colors" data-testid={`of-txn-${t.external_id}`}>
                                <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0" style={{ background: t.type === 'entrada' ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)' }}>
                                    {t.type === 'entrada' ? <ArrowUpRight size={12} style={{ color: '#10B981' }} /> : <ArrowDownLeft size={12} style={{ color: '#EF4444' }} />}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-xs font-medium truncate" style={{ color: 'var(--text)' }}>{t.description}</p>
                                    <div className="flex items-center gap-2 mt-0.5">
                                        <span className="text-[10px]" style={{ color: 'var(--muted)' }}>{t.date}</span>
                                        {editingTxn === t.external_id ? (
                                            <div className="flex items-center gap-1">
                                                <select value={editCat} onChange={e => setEditCat(e.target.value)} className="text-[10px] px-1 py-0.5 rounded" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }}>
                                                    {categories.map(c => <option key={c} value={c}>{c}</option>)}
                                                </select>
                                                <button onClick={() => handleUpdateCategory(t.external_id)} className="p-0.5"><Check size={10} style={{ color: '#10B981' }} /></button>
                                                <button onClick={() => setEditingTxn(null)} className="p-0.5"><X size={10} style={{ color: '#EF4444' }} /></button>
                                            </div>
                                        ) : (
                                            <button onClick={() => { setEditingTxn(t.external_id); setEditCat(t.category_manual || t.category); }} className="flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(var(--sv-accent-rgb), 0.08)', color: 'var(--sv-accent)' }}>
                                                <Tag size={8} /> {t.category_manual || t.category}
                                            </button>
                                        )}
                                        {t.is_recurring && <span className="text-[10px] px-1 rounded-full" style={{ background: 'rgba(139,92,246,0.1)', color: '#8B5CF6' }}>Recorrente</span>}
                                    </div>
                                </div>
                                <CurrencyDisplay value={t.amount} className="text-xs font-bold shrink-0" style={{ color: t.type === 'entrada' ? '#10B981' : '#EF4444' }} />
                            </div>
                        ))}
                    </div>
                )}

                {/* Pagination */}
                {txnTotal > 50 && (
                    <div className="flex justify-center gap-2 mt-3">
                        <button onClick={() => setTxnPage(p => Math.max(1, p - 1))} disabled={txnPage <= 1} className="px-3 py-1 rounded text-xs" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }}>Anterior</button>
                        <span className="text-xs py-1" style={{ color: 'var(--muted)' }}>{txnPage} / {Math.ceil(txnTotal / 50)}</span>
                        <button onClick={() => setTxnPage(p => p + 1)} disabled={txnPage >= Math.ceil(txnTotal / 50)} className="px-3 py-1 rounded text-xs" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)' }}>Proximo</button>
                    </div>
                )}
            </div>
            </>)}

            {/* REPORTS TAB */}
            {activeTab === 'relatorios' && execReport && (
                <div className="space-y-4" data-testid="of-reports-section">
                    {/* Period selector for reports */}
                    <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="flex gap-2 flex-wrap">
                            {PERIODS.map(p => (
                                <button key={p.key} onClick={() => { setPeriod(p.key); setTxnPage(1); }} className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all" style={{
                                    background: period === p.key ? 'var(--sv-secondary)' : 'var(--surface)',
                                    color: period === p.key ? 'var(--sv-on-secondary)' : 'var(--muted)',
                                    border: `1px solid ${period === p.key ? 'var(--sv-secondary)' : 'var(--border)'}`,
                                }}>{p.label}</button>
                            ))}
                        </div>
                        <div className="flex gap-1.5">
                            <button onClick={() => handleExport('csv')} disabled={!!exporting} className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs" style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text)' }} data-testid="of-report-export-csv">
                                {exporting === 'csv' ? <Loader2 size={11} className="animate-spin" /> : <FileSpreadsheet size={11} />} CSV
                            </button>
                            <button onClick={() => handleExport('pdf')} disabled={!!exporting} className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs" style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text)' }} data-testid="of-report-export-pdf">
                                {exporting === 'pdf' ? <Loader2 size={11} className="animate-spin" /> : <FileText size={11} />} PDF
                            </button>
                        </div>
                    </div>

                    {/* KPI Comparison Cards */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        {[
                            { label: 'Entradas', cur: execReport.current.entradas, prev: execReport.previous.entradas, pct: execReport.variation.entradas_pct, color: '#10B981', icon: ArrowUpRight },
                            { label: 'Saidas', cur: execReport.current.saidas, prev: execReport.previous.saidas, pct: execReport.variation.saidas_pct, color: '#EF4444', icon: ArrowDownLeft },
                            { label: 'Saldo', cur: execReport.current.saldo, prev: execReport.previous.saldo, pct: execReport.variation.saldo_pct, color: 'var(--sv-accent)', icon: Wallet },
                            { label: 'Transacoes', cur: execReport.current.transacoes, prev: execReport.previous.transacoes, pct: 0, color: '#6366F1', icon: BarChart3, isCount: true },
                        ].map(kpi => (
                            <div key={kpi.label} className="rounded-xl p-3" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                                <div className="flex items-center gap-1.5 mb-2">
                                    <kpi.icon size={12} style={{ color: kpi.color }} />
                                    <span className="text-[10px] font-medium" style={{ color: 'var(--muted)' }}>{kpi.label}</span>
                                </div>
                                <p className="text-sm font-bold" style={{ color: 'var(--text)' }}>
                                    {kpi.isCount ? kpi.cur : `R$ ${kpi.cur?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`}
                                </p>
                                <div className="flex items-center gap-1 mt-1">
                                    {kpi.pct !== 0 && (
                                        <span className="text-[10px] font-medium" style={{ color: kpi.pct > 0 ? (kpi.label === 'Saidas' ? '#EF4444' : '#10B981') : (kpi.label === 'Saidas' ? '#10B981' : '#EF4444') }}>
                                            {kpi.pct > 0 ? '+' : ''}{kpi.pct}%
                                        </span>
                                    )}
                                    <span className="text-[9px]" style={{ color: 'var(--muted)' }}>
                                        vs anterior: {kpi.isCount ? kpi.prev : `R$ ${kpi.prev?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Insights */}
                    {execReport.insights?.length > 0 && (
                        <div className="rounded-xl p-4" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                            <h3 className="text-xs font-semibold mb-3" style={{ color: 'var(--text)' }}>Insights</h3>
                            <div className="space-y-2">
                                {execReport.insights.map((ins, i) => (
                                    <div key={i} className="flex items-start gap-2 p-2 rounded-lg" style={{ background: ins.type === 'warning' ? 'rgba(239,68,68,0.06)' : ins.type === 'positive' ? 'rgba(16,185,129,0.06)' : 'rgba(99,102,241,0.06)', border: `1px solid ${ins.type === 'warning' ? 'rgba(239,68,68,0.15)' : ins.type === 'positive' ? 'rgba(16,185,129,0.15)' : 'rgba(99,102,241,0.15)'}` }}>
                                        {ins.type === 'warning' ? <AlertTriangle size={12} style={{ color: '#EF4444', marginTop: 2, flexShrink: 0 }} /> : ins.type === 'positive' ? <TrendingUp size={12} style={{ color: '#10B981', marginTop: 2, flexShrink: 0 }} /> : <Tag size={12} style={{ color: '#6366F1', marginTop: 2, flexShrink: 0 }} />}
                                        <p className="text-xs" style={{ color: 'var(--text)' }}>{ins.text}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Top Categories + Daily Evolution */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                        {/* Top categories */}
                        <div className="rounded-xl p-4" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                            <h3 className="text-xs font-semibold mb-3" style={{ color: 'var(--text)' }}>Top Categorias de Gasto</h3>
                            <div className="space-y-2">
                                {execReport.top_categories?.map((cat, i) => (
                                    <div key={i} className="flex items-center gap-2">
                                        <span className="text-[10px] w-4 font-bold" style={{ color: 'var(--muted)' }}>{i + 1}</span>
                                        <div className="flex-1">
                                            <div className="flex justify-between mb-1">
                                                <span className="text-xs" style={{ color: 'var(--text)' }}>{cat.name}</span>
                                                <span className="text-xs font-medium" style={{ color: 'var(--text)' }}>R$ {cat.value?.toLocaleString('pt-BR')}</span>
                                            </div>
                                            <div className="w-full h-1.5 rounded-full" style={{ background: 'var(--border)' }}>
                                                <div className="h-full rounded-full" style={{ width: `${Math.min(cat.pct, 100)}%`, background: ['#7DB3FF', '#10B981', '#6366F1', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6'][i % 8] }} />
                                            </div>
                                        </div>
                                        <span className="text-[10px] font-medium w-10 text-right" style={{ color: 'var(--muted)' }}>{cat.pct}%</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Daily evolution chart */}
                        <div className="rounded-xl p-4" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                            <h3 className="text-xs font-semibold mb-3" style={{ color: 'var(--text)' }}>Evolucao Diaria</h3>
                            {execReport.daily_evolution?.length > 0 ? (
                                <ResponsiveContainer width="100%" height={180}>
                                    <BarChart data={execReport.daily_evolution}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                                        <XAxis dataKey="date" tick={{ fontSize: 8, fill: 'var(--muted)' }} tickFormatter={d => d?.slice(5)} />
                                        <YAxis tick={{ fontSize: 8, fill: 'var(--muted)' }} tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
                                        <Tooltip contentStyle={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 10 }} formatter={v => `R$ ${v?.toLocaleString('pt-BR')}`} />
                                        <Bar dataKey="entradas" fill="#10B981" radius={[3,3,0,0]} />
                                        <Bar dataKey="saidas" fill="#EF4444" radius={[3,3,0,0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            ) : (
                                <p className="text-xs text-center py-8" style={{ color: 'var(--muted)' }}>Sem dados para o periodo</p>
                            )}
                        </div>
                    </div>

                    {/* Summary footer */}
                    <div className="rounded-xl p-3 flex items-center justify-between flex-wrap gap-2" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                        <div className="flex items-center gap-4">
                            <span className="text-[10px]" style={{ color: 'var(--muted)' }}><Landmark size={10} className="inline mr-1" />{execReport.connections || 0} contas conectadas</span>
                            <span className="text-[10px]" style={{ color: 'var(--muted)' }}><Bell size={10} className="inline mr-1" />{execReport.active_alerts || 0} alertas ativos</span>
                        </div>
                        <span className="text-[10px]" style={{ color: 'var(--muted)' }}>Dados do periodo: {PERIODS.find(p => p.key === period)?.label}</span>
                    </div>
                </div>
            )}

            {/* IMPORT TAB */}
            {activeTab === 'importar' && (
                <div className="space-y-4" data-testid="of-import-section">
                    <div className="rounded-xl p-5" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                        <div className="flex items-center gap-2 mb-4">
                            <Upload size={16} style={{ color: 'var(--sv-accent)' }} />
                            <h3 className="text-sm font-semibold" style={{ color: 'var(--text)', fontFamily: "var(--font-display)" }}>Importar Extrato Bancario</h3>
                        </div>
                        <p className="text-xs mb-4" style={{ color: 'var(--muted)', lineHeight: 1.6 }}>
                            Importe seus extratos manualmente nos formatos <strong>CSV</strong> ou <strong>OFX</strong>. As transacoes serao adicionadas ao seu painel financeiro.
                        </p>

                        {/* Format info */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                            <div className="p-3 rounded-lg" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)' }}>
                                <div className="flex items-center gap-2 mb-1">
                                    <FileSpreadsheet size={12} style={{ color: '#10B981' }} />
                                    <span className="text-xs font-semibold" style={{ color: 'var(--text)' }}>CSV</span>
                                </div>
                                <p className="text-[10px]" style={{ color: 'var(--muted)' }}>Colunas aceitas: Data, Descricao, Valor, Categoria. Separador: ponto-e-virgula ou virgula.</p>
                            </div>
                            <div className="p-3 rounded-lg" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)' }}>
                                <div className="flex items-center gap-2 mb-1">
                                    <FileText size={12} style={{ color: '#3B82F6' }} />
                                    <span className="text-xs font-semibold" style={{ color: 'var(--text)' }}>OFX</span>
                                </div>
                                <p className="text-[10px]" style={{ color: 'var(--muted)' }}>Formato bancario padrao. Exportado pela maioria dos bancos brasileiros.</p>
                            </div>
                        </div>

                        {/* Upload area */}
                        <label className="block cursor-pointer mb-3" data-testid="of-import-drop">
                            <input
                                type="file"
                                accept=".csv,.ofx"
                                className="hidden"
                                onChange={(e) => { setImportFile(e.target.files[0]); setImportResult(null); }}
                                data-testid="of-import-input"
                            />
                            <div className="border-2 border-dashed rounded-xl p-6 text-center transition-colors hover:border-[rgba(125,179,255,0.5)]" style={{ borderColor: importFile ? 'var(--sv-accent)' : 'var(--border)', background: importFile ? 'rgba(var(--sv-accent-rgb), 0.04)' : 'transparent' }}>
                                {importFile ? (
                                    <div className="flex items-center justify-center gap-2">
                                        <FileText size={16} style={{ color: 'var(--sv-accent)' }} />
                                        <span className="text-xs font-medium" style={{ color: 'var(--text)' }}>{importFile.name}</span>
                                        <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: 'rgba(var(--sv-accent-rgb), 0.12)', color: 'var(--sv-accent)' }}>
                                            {(importFile.size / 1024).toFixed(1)} KB
                                        </span>
                                        <button onClick={(e) => { e.preventDefault(); setImportFile(null); setImportResult(null); }} className="ml-2 p-1 rounded-md hover:bg-red-500/10">
                                            <X size={12} style={{ color: '#EF4444' }} />
                                        </button>
                                    </div>
                                ) : (
                                    <>
                                        <Upload size={24} style={{ color: 'var(--muted)', margin: '0 auto 8px' }} />
                                        <p className="text-xs" style={{ color: 'var(--muted)' }}>Clique para selecionar um arquivo <strong>.csv</strong> ou <strong>.ofx</strong></p>
                                    </>
                                )}
                            </div>
                        </label>

                        <button
                            onClick={handleImportFile}
                            disabled={!importFile || importing}
                            className="w-full py-2.5 rounded-lg text-xs font-semibold flex items-center justify-center gap-2 transition-all hover:scale-[1.01] disabled:opacity-40"
                            style={{ background: 'var(--sv-gold-btn)', color: 'var(--sv-on-action)' }}
                            data-testid="of-import-submit"
                        >
                            {importing ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                            {importing ? 'Importando...' : 'Importar transacoes'}
                        </button>

                        {/* Result */}
                        {importResult && (
                            <div className="mt-3 p-3 rounded-lg" style={{
                                background: importResult.ok ? 'rgba(34,197,94,0.06)' : 'rgba(239,68,68,0.06)',
                                border: `1px solid ${importResult.ok ? 'rgba(34,197,94,0.2)' : 'rgba(239,68,68,0.2)'}`,
                            }} data-testid="of-import-result">
                                <p className="text-xs font-medium" style={{ color: importResult.ok ? '#22c55e' : '#EF4444' }}>
                                    {importResult.ok ? `${importResult.imported} transacao(oes) importada(s)` : importResult.message}
                                </p>
                                {importResult.errors > 0 && (
                                    <p className="text-[10px] mt-1" style={{ color: '#F59E0B' }}>{importResult.errors} erro(s) encontrado(s)</p>
                                )}
                                {importResult.error_details?.length > 0 && (
                                    <div className="mt-2 space-y-0.5">
                                        {importResult.error_details.map((e, i) => (
                                            <p key={i} className="text-[9px]" style={{ color: 'var(--muted)' }}>{e}</p>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* RULES TAB */}
            {activeTab === 'regras' && (
                <div className="space-y-4" data-testid="of-rules-section">
                    <div className="rounded-xl p-4" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                        <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                                <Zap size={14} style={{ color: 'var(--sv-accent)' }} />
                                <h3 className="text-xs font-semibold" style={{ color: 'var(--text)' }}>Regras de Categorizacao ({rules.length})</h3>
                            </div>
                            <div className="flex gap-2">
                                {rules.length > 0 && (
                                    <button onClick={handleApplyRules} className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium" style={{ background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.3)', color: '#10B981' }} data-testid="of-apply-rules">
                                        <Zap size={11} /> Aplicar Regras
                                    </button>
                                )}
                                <button onClick={() => setShowRuleForm(!showRuleForm)} className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium" style={{ background: 'var(--sv-gold-btn)', color: 'var(--sv-on-action)' }} data-testid="of-add-rule">
                                    <Plus size={12} /> Nova Regra
                                </button>
                            </div>
                        </div>

                        {showRuleForm && (
                            <div className="p-3 mb-3 rounded-lg space-y-2" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)' }} data-testid="of-rule-form">
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                                    <input value={newRule.pattern} onChange={e => setNewRule(p => ({ ...p, pattern: e.target.value }))} placeholder="Padrao (ex: aluguel)"
                                        className="px-3 h-9 rounded-lg text-xs" style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text)' }} data-testid="of-rule-pattern" />
                                    <select value={newRule.category} onChange={e => setNewRule(p => ({ ...p, category: e.target.value }))}
                                        className="px-3 h-9 rounded-lg text-xs" style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text)' }} data-testid="of-rule-category">
                                        <option value="">Categoria destino</option>
                                        {categories.map(c => <option key={c} value={c}>{c}</option>)}
                                    </select>
                                    <select value={newRule.match_type} onChange={e => setNewRule(p => ({ ...p, match_type: e.target.value }))}
                                        className="px-3 h-9 rounded-lg text-xs" style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text)' }} data-testid="of-rule-match">
                                        <option value="contains">Contem</option>
                                        <option value="startswith">Comeca com</option>
                                        <option value="exact">Exato</option>
                                    </select>
                                </div>
                                <div className="flex gap-2 justify-end">
                                    <button onClick={() => setShowRuleForm(false)} className="px-3 py-1.5 rounded-lg text-xs" style={{ color: 'var(--muted)' }}>Cancelar</button>
                                    <button onClick={handleCreateRule} className="px-4 py-1.5 rounded-lg text-xs font-medium" style={{ background: 'var(--sv-gold-btn)', color: 'var(--sv-on-action)' }} data-testid="of-rule-save">Salvar</button>
                                </div>
                            </div>
                        )}

                        {rules.length === 0 ? (
                            <p className="text-xs py-4 text-center" style={{ color: 'var(--muted)' }}>Crie regras para categorizar transacoes automaticamente.</p>
                        ) : (
                            <div className="space-y-1">
                                {rules.map(r => (
                                    <div key={r.rule_id} className="flex items-center gap-3 p-2.5 rounded-lg" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)' }} data-testid={`of-rule-${r.rule_id}`}>
                                        <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0" style={{ background: 'rgba(var(--sv-accent-rgb), 0.1)' }}>
                                            <Tag size={12} style={{ color: 'var(--sv-accent)' }} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-xs font-medium" style={{ color: 'var(--text)' }}>
                                                Se descricao <span style={{ color: 'var(--sv-accent)' }}>{r.match_type === 'contains' ? 'contem' : r.match_type === 'startswith' ? 'comeca com' : 'igual a'}</span> "{r.pattern}"
                                            </p>
                                            <p className="text-[10px] mt-0.5" style={{ color: 'var(--muted)' }}>
                                                Categorizar como <span className="font-medium" style={{ color: '#10B981' }}>{r.category}</span> | {r.applied_count || 0} aplicacoes
                                            </p>
                                        </div>
                                        <button onClick={() => handleDeleteRule(r.rule_id)} className="p-2 rounded-lg" style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }} data-testid={`of-rule-del-${r.rule_id}`}>
                                            <Trash2 size={12} style={{ color: '#EF4444' }} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* ALERTS TAB */}
            {activeTab === 'alertas' && (
                <div className="space-y-4" data-testid="of-alerts-section">
                    {/* Triggered Alerts */}
                    {triggeredAlerts.length > 0 && (
                        <div className="rounded-xl p-4" style={{ background: 'rgba(239,68,68,0.05)', border: '1px solid rgba(239,68,68,0.2)' }} data-testid="of-triggered-alerts">
                            <div className="flex items-center gap-2 mb-3">
                                <AlertTriangle size={14} style={{ color: '#EF4444' }} />
                                <h3 className="text-xs font-semibold" style={{ color: '#EF4444' }}>Alertas Disparados</h3>
                            </div>
                            <div className="space-y-2">
                                {triggeredAlerts.map(a => (
                                    <div key={a.alert_id} className="flex items-center gap-3 p-3 rounded-lg" style={{ background: 'var(--surface)', border: '1px solid rgba(239,68,68,0.2)' }}>
                                        <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: 'rgba(239,68,68,0.1)' }}>
                                            <Bell size={14} style={{ color: '#EF4444' }} />
                                        </div>
                                        <div className="flex-1">
                                            <p className="text-xs font-semibold" style={{ color: 'var(--text)' }}>{a.name}</p>
                                            <p className="text-[10px]" style={{ color: 'var(--muted)' }}>
                                                Limite: R$ {a.threshold?.toLocaleString('pt-BR')} | Gasto: <span style={{ color: '#EF4444' }}>R$ {a.current_value?.toLocaleString('pt-BR')}</span> | Excedeu: R$ {a.exceeded_by?.toLocaleString('pt-BR')}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* All Alerts */}
                    <div className="rounded-xl p-4" style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
                        <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                                <Bell size={14} style={{ color: 'var(--sv-accent)' }} />
                                <h3 className="text-xs font-semibold" style={{ color: 'var(--text)' }}>Alertas Financeiros ({alerts.length})</h3>
                            </div>
                            <button onClick={() => setShowAlertForm(!showAlertForm)} className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium" style={{ background: 'var(--sv-gold-btn)', color: 'var(--sv-on-action)' }} data-testid="of-add-alert">
                                <Plus size={12} /> Novo Alerta
                            </button>
                        </div>

                        {showAlertForm && (
                            <div className="p-3 mb-3 rounded-lg space-y-2" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)' }} data-testid="of-alert-form">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                    <input value={newAlert.name} onChange={e => setNewAlert(p => ({ ...p, name: e.target.value }))} placeholder="Nome do alerta"
                                        className="px-3 h-9 rounded-lg text-xs" style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text)' }} data-testid="of-alert-name" />
                                    <input value={newAlert.threshold} onChange={e => setNewAlert(p => ({ ...p, threshold: e.target.value }))} placeholder="Limite (R$)" type="number"
                                        className="px-3 h-9 rounded-lg text-xs" style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text)' }} data-testid="of-alert-threshold" />
                                    <select value={newAlert.category} onChange={e => setNewAlert(p => ({ ...p, category: e.target.value }))}
                                        className="px-3 h-9 rounded-lg text-xs" style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text)' }} data-testid="of-alert-category">
                                        <option value="">Todas categorias</option>
                                        {categories.map(c => <option key={c} value={c}>{c}</option>)}
                                    </select>
                                    <select value={newAlert.period} onChange={e => setNewAlert(p => ({ ...p, period: e.target.value }))}
                                        className="px-3 h-9 rounded-lg text-xs" style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text)' }} data-testid="of-alert-period">
                                        <option value="week">Semanal</option>
                                        <option value="month">Mensal</option>
                                        <option value="quarter">Trimestral</option>
                                    </select>
                                </div>
                                <div className="flex gap-2 justify-end">
                                    <button onClick={() => setShowAlertForm(false)} className="px-3 py-1.5 rounded-lg text-xs" style={{ color: 'var(--muted)' }}>Cancelar</button>
                                    <button onClick={handleCreateAlert} className="px-4 py-1.5 rounded-lg text-xs font-medium" style={{ background: 'var(--sv-gold-btn)', color: 'var(--sv-on-action)' }} data-testid="of-alert-save">Salvar</button>
                                </div>
                            </div>
                        )}

                        {alerts.length === 0 ? (
                            <p className="text-xs py-4 text-center" style={{ color: 'var(--muted)' }}>Crie alertas para monitorar gastos por categoria ou periodo.</p>
                        ) : (
                            <div className="space-y-1">
                                {alerts.map(a => (
                                    <div key={a.alert_id} className="flex items-center gap-3 p-2.5 rounded-lg" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)' }} data-testid={`of-alert-${a.alert_id}`}>
                                        <div className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0" style={{ background: triggeredAlerts.some(t => t.alert_id === a.alert_id) ? 'rgba(239,68,68,0.1)' : 'rgba(16,185,129,0.1)' }}>
                                            <Bell size={12} style={{ color: triggeredAlerts.some(t => t.alert_id === a.alert_id) ? '#EF4444' : '#10B981' }} />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-xs font-medium" style={{ color: 'var(--text)' }}>{a.name}</p>
                                            <p className="text-[10px] mt-0.5" style={{ color: 'var(--muted)' }}>
                                                Limite: R$ {a.threshold?.toLocaleString('pt-BR')} | {a.category || 'Todas'} | {a.period === 'week' ? 'Semanal' : a.period === 'quarter' ? 'Trimestral' : 'Mensal'}
                                                {a.trigger_count > 0 && <span style={{ color: '#F59E0B' }}> | {a.trigger_count}x disparado</span>}
                                            </p>
                                        </div>
                                        <button onClick={() => handleDeleteAlert(a.alert_id)} className="p-2 rounded-lg" style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }} data-testid={`of-alert-del-${a.alert_id}`}>
                                            <Trash2 size={12} style={{ color: '#EF4444' }} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
